import Wkt from 'wicket';

import {
  STONE_CLASSES,
  NEUTRAL_STONE_CLASS,
  CAP_STEP,
  CAP_FLOOR_MAX
} from './fields.constants';

export const SORT_NAME = 'name';
export const SORT_DENSITY = 'density';

export const formatCoverage = (ratio, precision) => {
  const low = Math.round(ratio * 100);
  const high = Math.min(
    100,
    Math.round((ratio + (precision?.max_undercount ?? 0)) * 100)
  );
  return high > low ? `${low}–${high} %` : `${low} %`;
};

export const coverageOf = field => field.open_survey?.coverage_ratio ?? null;

export const densityOf = field =>
  field.open_survey && field.area_acres
    ? field.open_survey.stone_count / field.area_acres
    : null;

export const sortFields = (fields, sortKey) =>
  [...fields].sort((a, b) => {
    if (sortKey === SORT_DENSITY) {
      const byDensity = (densityOf(b) ?? -1) - (densityOf(a) ?? -1);
      if (byDensity !== 0) return byDensity;
    }
    return a.name.localeCompare(b.name);
  });

export const wktToGeoJson = wkt => {
  if (!wkt) return null;
  try {
    const parsed = new Wkt.Wkt();
    parsed.read(wkt);
    return parsed.toJson();
  } catch (error) {
    return null;
  }
};

export const openSurveyOf = detail => detail?.surveys?.find(survey => survey.is_open);

export const coveredFeatures = coverage => ({
  type: 'FeatureCollection',
  features: (coverage?.features ?? []).filter(
    feature => feature.properties.kind === 'covered'
  )
});

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const splitDay = isoDay => isoDay.split('-').map(Number);

export const formatShortDay = isoDay => {
  if (!isoDay) return '';
  const [year, month, day] = splitDay(isoDay);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

export const formatLongDay = isoDay => {
  if (!isoDay) return '';
  const [year, month, day] = splitDay(isoDay);
  return `${MONTHS[month - 1]} ${day}, ${year}`;
};

export const daysBetween = (from, to) =>
  Math.round((Date.parse(to) - Date.parse(from)) / 86400000);

export const surveyLabel = survey =>
  survey.closed_on
    ? `${formatShortDay(survey.opened_on)} – ${formatShortDay(survey.closed_on)}`
    : `Open since ${formatShortDay(survey.opened_on)}`;

export const surveyDaysOpen = (survey, today) =>
  daysBetween(survey.opened_on, survey.closed_on ?? today);

export const pluralize = (count, one, many) => (count === 1 ? one : many);

export const stoneClassOf = sizeClass =>
  STONE_CLASSES.find(({ key }) => key === sizeClass) ?? NEUTRAL_STONE_CLASS;

export const stoneSeenLabel = ({ first_seen: first, last_seen: last }) => {
  if (!first) return null;
  if (!last || last === first) return formatShortDay(first);
  return `${formatShortDay(first)} – ${formatShortDay(last)}`;
};

export const pickListCapMax = (stoneCount, acres) => {
  if (!acres) return CAP_FLOOR_MAX;
  const steps = 1 / CAP_STEP;
  return Math.max(CAP_FLOOR_MAX, Math.ceil((stoneCount / acres) * steps) / steps);
};

export const capForRequest = (cap, capMax) => (cap >= capMax ? null : cap);

export const formatCount = count => count.toLocaleString();

const strong = text => ({ text, tone: 'strong' });
const plain = text => ({ text });

export const pickListSentence = ({ preview, capPerAcre, stoneCount }) => {
  if (stoneCount === 0) return [plain('Nothing on this map yet.')];

  const { selected, held_back: heldBack, must_pick: mustPick } = preview;
  if (capPerAcre === null) {
    const rate = (selected / preview.area_acres).toFixed(2);
    return [
      plain('No cap — every match goes out, '),
      strong(`${rate} per acre`),
      plain('.')
    ];
  }

  const cap = strong(`${capPerAcre.toFixed(2)} per acre`);
  if (heldBack === 0 && mustPick > Math.round(capPerAcre * preview.area_acres)) {
    return [
      plain('All '),
      strong(formatCount(mustPick)),
      plain(
        ` red (${preview.must_pick_cm} cm+) go out regardless of the rate. Nothing else matched.`
      )
    ];
  }
  if (heldBack === 0) {
    return [plain('Everything that matched fits under '), cap, plain('.')];
  }

  const red =
    mustPick > 0
      ? [
          plain('All '),
          strong(formatCount(mustPick)),
          plain(` red (${preview.must_pick_cm} cm+) go out regardless of the rate. `)
        ]
      : [];
  return [
    ...red,
    strong(formatCount(heldBack)),
    plain(
      ` smaller ${pluralize(heldBack, 'yellow', 'yellows')} held back to stay under `
    ),
    cap,
    plain(' — they stay on the survey.')
  ];
};

export const sizeGauges = ({ stones, selectedIds }) => {
  const total = {};
  const onList = {};
  (stones?.features ?? []).forEach(({ properties }) => {
    const key = properties.size_class;
    total[key] = (total[key] ?? 0) + 1;
    if (selectedIds.has(properties.id)) onList[key] = (onList[key] ?? 0) + 1;
  });
  return STONE_CLASSES.map(({ key, label, color }) => ({
    key,
    label,
    color,
    total: total[key] ?? 0,
    onList: onList[key] ?? 0
  }));
};

export const gaugeCaption = ({ total, onList }) => {
  if (total === 0) return 'none found';
  if (onList === total) return 'all';
  return onList === 0 ? 'none' : '';
};

export const mustPickOverRate = ({ preview, capPerAcre }) => {
  if (capPerAcre === null || !preview.area_acres) return null;
  if (preview.must_pick <= Math.round(capPerAcre * preview.area_acres)) return null;
  const rate = (preview.must_pick / preview.area_acres).toFixed(2);
  return (
    `At ${preview.must_pick_cm} cm, ${formatCount(preview.must_pick)} stones on this ` +
    `field must be picked — ${rate} /ac. The rate slider can't bring that down. ` +
    'Raise the cm number.'
  );
};

export const rateEffect = ({ preview, gauges, stoneCount }) => {
  if (stoneCount === 0) {
    return [
      plain(
        'Nothing on this field yet. The next pass a unit runs here will start filling it in.'
      )
    ];
  }

  const { selected, held_back: heldBack } = preview;
  if (heldBack === 0) {
    const rate = preview.area_acres ? (selected / preview.area_acres).toFixed(2) : '0';
    return [
      plain('Everything that matched is going out — '),
      strong(formatCount(selected)),
      plain(` stones, ${rate} per acre. Pull the rate down to leave the smallest behind.`)
    ];
  }

  const bands = gauges.filter(({ total, onList }) => total > onList);
  const named = bands.flatMap(({ key, label, total, onList }, index) => [
    ...(index > 0 ? [plain(', ')] : []),
    { text: label, tone: key === 'l' || key === 'xl' ? 'warn' : 'band' },
    plain(` (${formatCount(total - onList)})`)
  ]);
  return [
    strong(formatCount(selected)),
    plain(' going out, '),
    strong(formatCount(heldBack)),
    plain(' left behind — smallest first, out of '),
    ...named,
    plain(`. Reds (${preview.must_pick_cm} cm+) are never cut.`)
  ];
};

export const stonesInList = (stones, ids) =>
  !stones || !ids
    ? stones
    : {
        ...stones,
        features: stones.features.filter(({ properties }) => ids.has(properties.id))
      };

export const gpxWarningText = ({ selected, maxWaypoints, maxCapPerAcre }) => {
  const limit = `A field screen reads at most ${formatCount(
    maxWaypoints
  )} waypoints and this list has ${formatCount(selected)}.`;
  return maxCapPerAcre === null
    ? `${limit} Even the red stones alone are over the limit. Choose another format.`
    : `${limit} Drop the cap to about ${maxCapPerAcre.toFixed(
        2
      )} per acre, or choose another format.`;
};

export const downloadedSummary = ({
  selected,
  mustPick,
  mustPickCm,
  fieldName,
  surveyTotal
}) =>
  `${formatCount(mustPick)} red (${mustPickCm} cm+, always pick) and ${formatCount(
    selected - mustPick
  )} yellow, ${formatCount(
    selected
  )} stones on ${fieldName}. Each point carries size_cm and source as well as the verdict, so the field screen can apply its own threshold. The survey keeps all ${formatCount(
    surveyTotal
  )}.`;

export const finaliseErrorMessage = error =>
  typeof error?.data === 'string' && error.data
    ? error.data
    : 'Could not finalise the survey. Try again.';

export const exportRateLabel = capPerAcre =>
  capPerAcre === null ? 'No cap' : `${capPerAcre.toFixed(2)} /ac`;

export const exportedStoneCounts = (stones, mustPickCm) => {
  const exported = (stones?.features ?? []).filter(
    ({ properties }) => properties.exported
  );
  const red = exported.filter(
    ({ properties }) => properties.size_cm != null && properties.size_cm >= mustPickCm
  ).length;
  return { red, yellow: exported.length - red };
};

export const frozenStoneCounts = (stones, mustPickCm) => {
  const features = stones?.features ?? [];
  const byClass = {};
  features.forEach(({ properties }) => {
    byClass[properties.size_class] = (byClass[properties.size_class] ?? 0) + 1;
  });
  const red = features.filter(
    ({ properties }) => properties.size_cm != null && properties.size_cm >= mustPickCm
  ).length;
  return { byClass, red };
};

export const fieldHeaderStats = ({
  acres,
  survey,
  isRecord,
  draft,
  densityConfidence
}) => {
  const total = survey.stone_count;
  const listed = !isRecord && draft ? draft.selected : total;
  const capped = !isRecord && Boolean(draft) && listed < total;
  const fieldDensity = acres ? total / acres : 0;
  const pickRate = acres ? listed / acres : 0;
  const isAtLeast = !isRecord && densityConfidence === 'at_least';
  const atLeast = isAtLeast ? 'at least ' : '';

  let stonesSub = null;
  if (isRecord) stonesSub = `frozen ${formatShortDay(survey.closed_on)}`;
  else if (capped) stonesSub = `of ${formatCount(total)} on record`;

  let densitySub = null;
  if (capped) densitySub = `field reads ${atLeast}${fieldDensity.toFixed(2)}`;
  else if (isAtLeast) densitySub = 'at least — part of the field is not imaged yet';

  return {
    stones: {
      value: formatCount(isRecord ? total : listed),
      label: capped ? 'stones to pick' : 'stones on record',
      sub: stonesSub
    },
    density: {
      value: (isRecord ? fieldDensity : pickRate).toFixed(2),
      label: 'stones per acre',
      sub: densitySub
    }
  };
};

export const STONE_LIST_LIMIT = 60;

export const listedStones = (stones, ids, limit = STONE_LIST_LIMIT) => {
  if (!stones || !ids) return [];
  const wanted = new Set(ids);
  return stones.features
    .map(({ properties }) => properties)
    .filter(stone => wanted.has(stone.id))
    .sort((a, b) => (b.size_cm ?? -1) - (a.size_cm ?? -1) || a.id - b.id)
    .slice(0, limit);
};
